/**
 * Storefront form validation.
 *
 * Attaches to every `form[data-validate]` and validates its fields on blur and on submit,
 * so nothing invalid ever reaches Shopify. Rules are declared per field:
 *
 *   native            required, minlength, maxlength, pattern, min, max, step
 *   data-validate-type       email | tel | name | password | zip | number | date | text
 *   data-validate-match      selector of the field this one must equal (password confirm)
 *   data-validate-min-date   "today" — rejects dates in the past
 *   data-validate-country    selector of the country <select> that scopes zip/tel rules
 *   data-validate-message    overrides the generated message for this field
 *
 * Messages come from FoxTheme.validationStrings (see snippets/js-variables.liquid).
 */
(function () {
  const FALLBACK_STRINGS = {
    required: 'Este campo es obligatorio.',
    requiredSelect: 'Elegí una opción.',
    requiredCheckbox: 'Tenés que marcar esta casilla para continuar.',
    email: 'Ingresá un email válido, por ejemplo nombre@dominio.com.',
    tel: 'Ingresá un teléfono válido, por ejemplo 11 2345 6789.',
    name: 'Ingresá un nombre válido (solo letras).',
    zip: 'Ingresá un código postal válido, por ejemplo 1425 o C1425ABC.',
    date: 'Ingresá una fecha válida.',
    datePast: 'La fecha no puede ser anterior a hoy.',
    number: 'Ingresá un número válido.',
    minLength: 'Ingresá al menos [min] caracteres.',
    maxLength: 'No puede superar los [max] caracteres.',
    min: 'El valor mínimo es [min].',
    max: 'El valor máximo es [max].',
    step: 'El valor debe ser múltiplo de [step].',
    pattern: 'El formato ingresado no es válido.',
    match: 'Los valores no coinciden.',
    passwordLength: 'La contraseña debe tener entre 5 y 40 caracteres.',
    summary: 'Revisá los campos marcados en rojo.',
  };

  const SELECTORS = {
    form: 'form[data-validate]',
    field: '.form-field',
    errorClass: 'form-field--error',
    errorMessage: 'form-validation-message',
  };

  const FOCUSABLE = 'input, select, textarea';

  const strings = Object.assign({}, FALLBACK_STRINGS, (window.FoxTheme && window.FoxTheme.validationStrings) || {});

  /** Replaces the `[min]` / `[max]` / `[step]` tokens the theme uses for JS-side translations. */
  const interpolate = (template, replacements) =>
    Object.keys(replacements).reduce(
      (result, key) => result.split('[' + key + ']').join(replacements[key]),
      template
    );

  /**
   * Letters (any alphabet), marks, spaces and the punctuation real names use.
   * Falls back to a Latin-1 range where Unicode property escapes are unsupported.
   */
  const nameRegExp = (function () {
    try {
      return new RegExp("^\\p{L}[\\p{L}\\p{M}\\s'’.\\-]*$", 'u');
    } catch (error) {
      return /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'’.\-]*$/;
    }
  })();

  const validators = {
    email(value) {
      if (/\s/.test(value) || value.indexOf('..') !== -1) return false;
      return /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9\-]+(\.[A-Za-z0-9\-]+)*\.[A-Za-z]{2,}$/.test(value);
    },

    /**
     * Argentina-first, but tolerant: accepts +54…, 0 / 15 prefixes, spaces,
     * dashes and parentheses. Any international number in E.164 also passes.
     */
    tel(value) {
      const hasPlus = /^\s*\+/.test(value);
      const digits = value.replace(/\D/g, '');

      if (!digits || /^(\d)\1+$/.test(digits)) return false;
      if (hasPlus) return digits.length >= 8 && digits.length <= 15;

      let local = digits;
      if (local.indexOf('54') === 0 && local.length > 11) local = local.slice(2);
      if (local.indexOf('0') === 0) local = local.slice(1);
      if (local.indexOf('9') === 0 && local.length === 11) local = local.slice(1);
      if (local.length === 12) {
        const withoutFifteen = local.match(/^(\d{2,4})15(\d+)$/);
        if (withoutFifteen) local = withoutFifteen[1] + withoutFifteen[2];
      }

      return /^\d{10}$/.test(local);
    },

    name(value) {
      return value.length >= 2 && nameRegExp.test(value);
    },

    /** Argentine CPA (C1425ABC) or the legacy 4-digit code. Other countries: any 3+ chars. */
    zip(value, field) {
      if (isArgentina(field)) return /^([A-Za-z]\d{4}[A-Za-z]{3}|\d{4})$/.test(value);
      return value.length >= 3;
    },

    number(value) {
      return value !== '' && !isNaN(Number(value));
    },

    date(value) {
      return !isNaN(new Date(value).getTime());
    },
  };

  function isArgentina(field) {
    const selector = field.getAttribute('data-validate-country');
    if (!selector) return true;

    const countrySelect = field.form && field.form.querySelector(selector);
    if (!countrySelect) return true;

    return /^(argentina|ar)$/i.test(countrySelect.value.trim());
  }

  function fieldWrapper(field) {
    return field.closest(SELECTORS.field) || field.parentElement;
  }

  function radioGroup(field) {
    if (field.type !== 'radio' || !field.form || !field.name) return null;
    return Array.prototype.slice.call(field.form.querySelectorAll('input[type="radio"][name="' + field.name + '"]'));
  }

  /** Returns the message for the first broken rule, or an empty string when the field is valid. */
  function checkField(field) {
    if (field.disabled || field.type === 'hidden' || field.readOnly) return '';
    if (field.closest('[hidden]')) return '';

    const custom = field.getAttribute('data-validate-message');
    const type = field.getAttribute('data-validate-type') || '';
    // Passwords are never trimmed — leading/trailing spaces are part of the secret.
    const raw = typeof field.value === 'string' ? field.value : '';
    const value = field.type === 'password' ? raw : raw.trim();
    const fail = (message) => custom || message;

    if (field.type === 'checkbox') {
      return field.required && !field.checked ? fail(strings.requiredCheckbox) : '';
    }

    const group = radioGroup(field);
    if (group) {
      const isRequired = group.some((radio) => radio.required);
      const isChecked = group.some((radio) => radio.checked);
      return isRequired && !isChecked ? fail(strings.requiredSelect) : '';
    }

    if (!value) {
      if (!field.required) return '';
      return fail(field.tagName === 'SELECT' ? strings.requiredSelect : strings.required);
    }

    const minLength = parseInt(field.getAttribute('minlength'), 10);
    if (minLength && value.length < minLength) {
      return fail(interpolate(strings.minLength, { min: minLength }));
    }

    const maxLength = parseInt(field.getAttribute('maxlength'), 10);
    if (maxLength && value.length > maxLength) {
      return fail(interpolate(strings.maxLength, { max: maxLength }));
    }

    if (type === 'password') {
      if (value.length < 5 || value.length > 40) return fail(strings.passwordLength);
    }

    if (validators[type] && !validators[type](value, field)) {
      return fail(strings[type] || strings.pattern);
    }

    if (field.type === 'number' || type === 'number') {
      if (isNaN(Number(value))) return fail(strings.number);

      const numeric = Number(value);
      const min = parseFloat(field.getAttribute('min'));
      const max = parseFloat(field.getAttribute('max'));
      const step = parseFloat(field.getAttribute('step'));

      if (!isNaN(min) && numeric < min) return fail(interpolate(strings.min, { min: min }));
      if (!isNaN(max) && numeric > max) return fail(interpolate(strings.max, { max: max }));
      if (!isNaN(step) && step > 0 && Math.abs((numeric - (isNaN(min) ? 0 : min)) % step) > 1e-9) {
        return fail(interpolate(strings.step, { step: step }));
      }
    }

    if (field.getAttribute('data-validate-min-date') === 'today') {
      const picked = new Date(value + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isNaN(picked.getTime())) return fail(strings.date);
      if (picked < today) return fail(strings.datePast);
    }

    const patternSource = field.getAttribute('pattern');
    if (patternSource && !new RegExp('^(?:' + patternSource + ')$').test(value)) {
      return fail(strings.pattern);
    }

    const matchSelector = field.getAttribute('data-validate-match');
    if (matchSelector) {
      const counterpart = field.form && field.form.querySelector(matchSelector);
      const other = counterpart && (counterpart.type === 'password' ? counterpart.value : counterpart.value.trim());
      if (counterpart && other !== value) return fail(strings.match);
    }

    return '';
  }

  function clearError(field) {
    const wrapper = fieldWrapper(field);
    wrapper.classList.remove(SELECTORS.errorClass);

    const message = wrapper.querySelector('.' + SELECTORS.errorMessage);
    if (message) message.remove();

    field.removeAttribute('aria-invalid');

    const describedBy = (field.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter((id) => id && id.indexOf('-client-error') === -1)
      .join(' ');

    if (describedBy) {
      field.setAttribute('aria-describedby', describedBy);
    } else {
      field.removeAttribute('aria-describedby');
    }
  }

  function showError(field, message) {
    const wrapper = fieldWrapper(field);
    wrapper.classList.add(SELECTORS.errorClass);
    field.setAttribute('aria-invalid', 'true');

    let element = wrapper.querySelector('.' + SELECTORS.errorMessage);

    if (!element) {
      element = document.createElement('span');
      element.className = SELECTORS.errorMessage + ' form-message form-message--small form-message--error';
      element.setAttribute('role', 'alert');
      element.id = (field.id || field.name || 'field').replace(/[^\w\-]/g, '') + '-client-error';
      wrapper.appendChild(element);
    }

    // Radio groups share one wrapper, so every radio points at the same message element.
    const errorId = element.id;
    element.textContent = message;

    const describedBy = (field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
    if (describedBy.indexOf(errorId) === -1) describedBy.push(errorId);
    field.setAttribute('aria-describedby', describedBy.join(' '));
  }

  function validateField(field) {
    const message = checkField(field);

    if (message) {
      showError(field, message);
    } else {
      clearError(field);
    }

    return !message;
  }

  class FormValidator {
    constructor(form) {
      this.form = form;
      this.form.setAttribute('novalidate', 'novalidate');
      this.form.validator = this;

      // Capture so this runs before the theme's own submit handlers (product form, cart, …).
      this.form.addEventListener('submit', this.onSubmit.bind(this), true);
      // Some theme forms (shipping calculator) act on the button click and never fire `submit`.
      this.form.addEventListener('click', this.onClick.bind(this), true);
      this.form.addEventListener('blur', this.onBlur.bind(this), true);
      this.form.addEventListener('input', this.onInput.bind(this), true);
      this.form.addEventListener('change', this.onInput.bind(this), true);
    }

    get fields() {
      return Array.prototype.slice.call(this.form.querySelectorAll(FOCUSABLE)).filter(
        (field) => !field.disabled && field.type !== 'hidden' && field.type !== 'submit' && field.type !== 'button'
      );
    }

    onBlur(event) {
      if (event.target.matches && event.target.matches(FOCUSABLE)) validateField(event.target);
    }

    /** Only re-validate while typing once the field has already been flagged, to avoid nagging. */
    onInput(event) {
      const field = event.target;
      if (!field.matches || !field.matches(FOCUSABLE)) return;
      if (event.type === 'change') this.revalidateCountryScoped(field);
      if (event.type === 'input' && !fieldWrapper(field).classList.contains(SELECTORS.errorClass)) return;
      validateField(field);
    }

    /** Switching country changes what a valid zip/phone looks like. */
    revalidateCountryScoped(field) {
      if (field.tagName !== 'SELECT') return;

      this.form.querySelectorAll('[data-validate-country]').forEach((scoped) => {
        const target = this.form.querySelector(scoped.getAttribute('data-validate-country'));
        if (target === field && fieldWrapper(scoped).classList.contains(SELECTORS.errorClass)) {
          validateField(scoped);
        }
      });
    }

    validate() {
      const invalid = this.fields.filter((field) => !validateField(field));
      return invalid.length === 0 ? null : invalid[0];
    }

    /** Trims text values so whitespace-only input never counts as filled in. */
    trimValues() {
      this.fields.forEach((field) => {
        if (typeof field.value === 'string' && /^(text|email|tel|search|url|textarea)$/.test(field.type)) {
          field.value = field.value.trim();
        }
      });
    }

    onClick(event) {
      const submitter = event.target.closest && event.target.closest('button, input[type="submit"]');
      if (!submitter || submitter.form !== this.form) return;

      const type = (submitter.getAttribute('type') || 'submit').toLowerCase();
      if (type !== 'submit') return;

      this.block(event, submitter);
    }

    onSubmit(event) {
      this.block(event, event.submitter);
    }

    /** Stops the event dead when anything is invalid, and sends the user to the first bad field. */
    block(event, submitter) {
      // `formnovalidate` submitters (e.g. "decline invitation") bypass the rules, as in plain HTML.
      if (submitter && submitter.hasAttribute('formnovalidate')) return;

      this.trimValues();
      const firstInvalid = this.validate();
      if (!firstInvalid) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      firstInvalid.focus({ preventScroll: true });
      if (firstInvalid.scrollIntoView) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTORS.form).forEach((form) => {
      if (form.validator || form.getAttribute('data-validate') === 'false') return;
      new FormValidator(form);
    });
  }

  // init() is idempotent, so run it now and again once the DOM is complete.
  init();
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.FormValidator = FormValidator;
  window.FoxTheme.initFormValidation = init;
})();
